<template>
    <v-layout class="print-container">
        <!-- large screens navigation drawer -->
        <v-navigation-drawer
            v-if="isAuthenticated"
            v-model="drawer"
            :class="[
                'no-print',
                display.smAndDown ? 'mobile-navbar' : 'navbar d-print-none',
            ]"
            location="start"
            :temporary="display.smAndDown"
            :permanent="display.mdAndUp"
        >
            <icon-button
                v-if="display.smAndDown"
                data-cy="mobileMenuCloseButton"
                btn-style="default-btn medium-icon-btn transparent-btn ml-8 my-4"
                :icon="mdiClose"
                @run-method="drawer = false"
            />

            <navbar />
        </v-navigation-drawer>

        <v-main>
            <!-- app bar-->
            <v-app-bar
                v-if="isAuthenticated"
                class="appbar no-print d-print-none"
                :class="{ 'appbar--organisation-accent': appBarAccent }"
                :style="appBarAccent ? { '--org-accent': appBarAccent } : {}"
                elevation="0"
                density="compact"
            >
                <!-- trigger for mobile navigation -->
                <icon-button
                    v-if="display.smAndDown"
                    data-cy="mobileMenuButton"
                    btn-style="default-btn medium-icon-btn transparent-btn ml-5"
                    :icon="drawer ? mdiClose : mdiMenu"
                    @run-method="drawer = !drawer"
                />

                <school-app-bar v-if="hasOrganisationPermissions" />
                <unauthorised-app-bar v-else />
            </v-app-bar>

            <!-- page content -->
            <v-container class="main" fluid>
                <compliance-warning
                    v-if="displayComplianceWarning"
                    class="mb-4 px-4 py-2 no-print"
                    cypress-data="kycWarning"
                />
                <card-provider-onboarding-warning
                    v-if="displayCardOnboardingWarning"
                    class="mb-4 px-4 py-2 no-print"
                    cypress-data="cardOnboardingWarning"
                />

                <!-- Organisation switch overlay -->
                <v-overlay
                    :model-value="organisationStore.isSwitching"
                    contained
                    persistent
                    scrim="false"
                    class="organisation-switch-overlay"
                >
                    <v-progress-circular indeterminate size="32" />
                </v-overlay>

                <!-- Keyed on the organisation context only, so switching organisation remounts
                     the page while ordinary navigation keeps vue-router's default reuse. -->
                <router-view
                    :key="
                        isOrganisationRoute
                            ? `organisation:${organisationStore.contextVersion}`
                            : undefined
                    "
                />

                <alert
                    v-model="alertConfiguration.visible"
                    :alert-message="alertConfiguration.message"
                    :alert-color="alertConfiguration.color"
                    :internationalised-message="
                        alertConfiguration.internationalisedMessage
                    "
                    :alert-parameters="alertConfiguration.alertParameters"
                ></alert>

                <updated-access-pop-up
                    :dialog-visible="showAccessPopUp"
                    @update:dialog-visible="updatePopUpVisible"
                ></updated-access-pop-up>

                <session-timeout-pop-up
                    :dialog-visible="showSessionPopUp"
                    :countdown="countdown"
                    @update:dialogVisible="showSessionPopUp = $event"
                    @stay-signed-in="staySignedIn"
                />
            </v-container>
        </v-main>
    </v-layout>
</template>

<script setup lang="ts">
import { usePiniaStore } from "@/stores/store";
import { useAuthenticator } from "@aws-amplify/ui-vue";
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useOrganisationStore } from "@/stores/organisationStore";

import iconButton from "@/components/buttons/iconButton.vue";
import alert from "@/components/misc/alert.vue";
import cardProviderOnboardingWarning from "@/components/misc/cardOnboardingWarning.vue";
import complianceWarning from "@/components/misc/complianceWarning.vue";
import navbar from "@/components/structural/NavBar.vue";
import unauthorisedAppBar from "./components/structural/UnauthorisedAppBar.vue";
import SchoolAppBar from "@/components/structural/SchoolAppBar.vue";
import sessionTimeoutPopUp from "@/components/structural/sessionTimeoutPopUp.vue";
import updatedAccessPopUp from "@/components/structural/updatedAccessPopUp.vue";

import { checkUserOrganisations } from "@/apis/esendaV2/user";
import { endInactiveUserSession } from "@/cognito-auth/sessionManagement";
import { OrganisationRouteNames } from "@/constants/routes";
import { alertConfiguration } from "@/methods/errorHandling";
import { setSentryUser } from "@/methods/sentry";
import { isEmptyOrUndefinedString } from "@/methods/validationRules";
import { useHelpScoutBeacon } from "@/composables/useHelpScoutBeacon";
import { mdiClose, mdiMenu } from "@mdi/js";

const auth = useAuthenticator();
const pStore = usePiniaStore();
const organisationStore = useOrganisationStore();
const route = useRoute();

/** Load helpscout support widget */
const beaconId = import.meta.env.VITE_HELPSCOUT_BEACON_ID;
useHelpScoutBeacon(beaconId);

const isAuthenticated = ref(auth.authStatus === "authenticated");

const displayComplianceWarning = computed(() => {
    // !!! Temporarily hiding Sokin compliance sections, so compliance warning will not be shown.
    // const complianceRoutes = [
    //     OrganisationRouteNames.COMPLIANCE,
    //     OrganisationRouteNames.COMPANY_DETAILS,
    //     OrganisationRouteNames.NATURE_OF_BUSINESS,
    //     OrganisationRouteNames.FINANCIAL_SYSTEM,
    //     OrganisationRouteNames.GOVERNANCE,
    //     OrganisationRouteNames.BANK_ACCOUNT_INFORMATION,
    // ];
    // return (
    //     isAuthenticated.value &&
    //     !complianceRoutes.includes(route.name as OrganisationRouteNames)
    // );

    return false;
});

const displayCardOnboardingWarning = computed(() => {
    const cardOnboardingRoutes = [
        OrganisationRouteNames.CARD_PROVIDER_ONBOARDING,
    ];
    return (
        isAuthenticated.value &&
        !route.meta.customerOnboarding &&
        !cardOnboardingRoutes.includes(route.name as OrganisationRouteNames)
    );
});

/**
 * Determines if the current route is an organisation route based on the presence of required permissions in the route meta.
 */
const isOrganisationRoute = computed(
    () =>
        !route.meta.customerOnboarding &&
        route.matched.some(
            ({ meta }) =>
                Array.isArray(meta.requiredPermissions) &&
                meta.requiredPermissions.length > 0,
        ),
);

watch(
    () => organisationStore.contextVersion,
    async () => {
        await nextTick();
        organisationStore.finishSwitching();
    },
    { immediate: true },
);

// breakpoints
const display = ref(useDisplay());

// navigation drawer - default to true if not on smaller device
const drawer = ref(display.value.mdAndUp || false);

const hasOrganisationPermissions = computed(
    () => Object.keys(pStore.getPermissions ?? {}).length > 0,
);

const appBarAccent = computed(() =>
    hasOrganisationPermissions.value &&
    organisationStore.getAvailableOrganisations.length > 1
        ? organisationStore.getActiveOrganisationColour
        : null,
);

const isHubpay = () => {
    const host = window.location.host;
    pStore.setIsHubpay(host.includes("hubpay"));
};

isHubpay();

const showAccessPopUp = ref(false);
const permissionCheckInFlight = ref(false);
const updatePopUpVisible = (value: boolean) => {
    showAccessPopUp.value = value;
};
const getPermissions = async () => {
    if (permissionCheckInFlight.value) return;

    const userEmail = pStore.getUserEmail;
    if (userEmail === null) return;

    permissionCheckInFlight.value = true;
    try {
        showAccessPopUp.value = await checkUserOrganisations(userEmail);
    } finally {
        permissionCheckInFlight.value = false;
    }
};

let intervalId;
const runIntervalPermissionsCheck = () => {
    /** Check permissions every 1 min */
    intervalId = setInterval(() => {
        getPermissions();
    }, 60000);
};

// The organisation context is hydrated by the router guard before protected
// route components mount, so this only mirrors the Amplify auth status.
watch(
    () => [auth.authStatus, pStore.getIsAuthenticated, route.fullPath],
    () => {
        isAuthenticated.value = auth.authStatus === "authenticated";
    },
    { immediate: true },
);

const showSessionPopUp = ref(false);
const countdown = ref(60);

const updateSessionPopUp = (
    visible: boolean,
    countdownValue?: number,
): void => {
    /**
     * Update the session timeout pop up visibility and countdown
     * @param {boolean} visible - whether the pop up should be visible
     * @param {number} countdownValue - the countdown value in seconds
     * @returns {void}
     */
    showSessionPopUp.value = visible;
    if (countdownValue !== undefined) countdown.value = countdownValue;
};

/** Log out user after 5 mins of inactivity */
const { staySignedIn } = endInactiveUserSession(5, updateSessionPopUp);

onMounted(() => {
    /** Update the user email in sentry if the auth email is not empty */
    const userEmail = pStore.getUserEmail;
    if (!isEmptyOrUndefinedString(userEmail)) {
        setSentryUser(userEmail as string);
    }
    runIntervalPermissionsCheck();
});

onBeforeUnmount(() => {
    /** Clean up the interval when the component is destroyed */
    if (intervalId) {
        clearInterval(intervalId);
    }
});
</script>
